import { Component } from "react";
import { connect } from "react-redux";
import { compose } from "recompose";
import produce from "immer";
import loadImage from "blueimp-load-image";
import { withTranslation } from "react-i18next";
import Dialog from "src/components/Dialog";
import GenericFailureDialog from "src/components/GenericFailureDialog";
import { withApp } from "src/components/App/Context";
import environment, { getAppValue } from "src/utils/environment";
import avatarFiles from "src/modules/avatar/avatar-files";
import { encodeBabyCustomization } from "src/modules/babyCustomization-protobuf";
import { encodeDogCustomization } from "src/modules/dogCustomization-protobuf";

import * as actions from "../../duck/actions";
import * as ajax from "../../ajax/cloud";
import Cropper from "./Cropper";
import DogView from "./DogView";
import BabyView from "./BabyView";

import { v4 as uuid4 } from "uuid";
import { AvatarModes } from "../../enum";
import time from "src/utils/time";
const path = window.require("path");
const log = window.require("electron-log");
const { dialog } = window.require("@electron/remote");
const { isBrowser } = environment;

const getAvatarDefaults = (avatar) => {
  const {
    objectId = null,
    uuid = uuid4(),
    name = "",
    sex = "male",
    previewMode = AvatarModes.illustration,
    moreDataJson = getAppValue({
      barkio: { breed: "" },
      bibino: { skinColor: "FITZ-2" }
    }),
    breed = "",
    avatarCustomization = getAppValue({
      bibino: {
        skinColor: "FITZ-2",
        dressColor: "#2aa598",
        accessoryColor: "#2aa598",
        hairColor: "#4e3727",
        clothes: "NAPPY",
        accessory: "NO_ACCESSORY",
        hair: "HAIR_0",
        nose: "NOSE_0",
        cheek: "CHEEK_2",
        eye: "EYE_0"
      },
      barkio: {
        breedAppearance:
          avatar?.avatarCustomization?.breedAppearance || "default"
      }
    }),
    birthDate = "",
    profileImageFile = undefined
  } = avatar || {};
  return {
    objectId,
    uuid,
    name,
    sex,
    previewMode,
    moreDataJson,
    breed,
    avatarCustomization,
    birthDate,
    profileImageFile
  };
};

class AvatarEditor extends Component {
  state = {
    isProcessing: false,
    isDeleting: false,
    showDeleteDialog: false,
    error: false,
    cropper: {
      isOpened: false,
      originalImageSrc: null,
      croppedImageDataUrl: null
    },
    avatar: getAvatarDefaults(this.props.avatar)
  };
  didCustomize = false;

  componentDidMount = () => {
    if (this.props.avatar) {
      this.didCustomize = true;
    }
  };

  handleSubmit = async (e) => {
    e.preventDefault();

    const { avatar, cropper } = this.state;
    let {
      uuid,
      name,
      sex,
      breed,
      moreDataJson,
      avatarCustomization: customization,
      birthDate,
      profileImageFile,
      objectId,
      previewMode
    } = avatar;
    const { croppedImageDataUrl } = cropper;
    const { dispatch, afterSave, app } = this.props;

    if (name === "") {
      return;
    }
    if (birthDate && !time(birthDate).isBetween("1900-01-01", new Date())) {
      return;
    }

    this.setState({ isProcessing: true });

    let newAvatar;
    let newProfileImageFile;

    try {
      const birthDateTime = birthDate ? new Date(birthDate).getTime() : null;
      const avatarCustomization = getAppValue({
        bibino: () => encodeBabyCustomization(customization),
        barkio: () => encodeDogCustomization(customization)
      })();
      if (croppedImageDataUrl) {
        const base64Image = croppedImageDataUrl.split(",")[1];
        newProfileImageFile = await ajax.saveProfileImage(base64Image);
        if (!isBrowser) {
          await avatarFiles.write(newProfileImageFile.name, base64Image, {
            encoding: "base64"
          });
        }
      }
      if (objectId == null) {
        newAvatar = await ajax.createAvatar({
          birthDateTime,
          profileImageFile: newProfileImageFile,
          uuid,
          moreDataJson,
          avatarCustomization,
          name,
          sex,
          previewMode
        });
      } else {
        let dataJson = moreDataJson;
        if (moreDataJson.breed === "") {
          dataJson = { breed: breed || "" };
        }

        newAvatar = await ajax.updateAvatar({
          avatarId: this.state.avatar.objectId,
          birthDateTime,
          profileImageFile: newProfileImageFile
            ? newProfileImageFile
            : profileImageFile,
          breed: "",
          moreDataJson: dataJson,
          avatarCustomization,
          previewMode,
          name,
          sex
        });
        if (newProfileImageFile && profileImageFile && isBrowser) {
          avatarFiles.delete(profileImageFile.name);
        }
      }
      dispatch(actions.saveAvatar(newAvatar));
      afterSave(newAvatar.objectId);

      app.synchronizer.announceItemChange(
        app.synchronizer.SynchronizableItems.AVATARS
      );
    } catch (error) {
      log.error(error);
      this.setState({ error: true, isProcessing: false });
    }
  };

  handleAvatarStyleUpdate = (update, isCustomizing = true) => {
    this.setState(({ avatar }) => ({
      avatar: {
        ...avatar,
        avatarCustomization: { ...avatar.avatarCustomization, ...update }
      }
    }));
    if (isCustomizing) this.didCustomize = true;
  };

  handleInputChange = (e) => {
    const { name, value } = e.target;
    if (name === "sex" && !this.didCustomize) {
      const isFemale = value === "female";
      this.handleAvatarStyleUpdate(
        {
          accessory: isFemale ? "BOW" : "NO_ACCESSORY",
          dressColor: isFemale ? "#fa48ad" : "#2aa598",
          accessoryColor: isFemale ? "#fa48ad" : "#2aa598"
        },
        false
      );
    }
    if (name === "breed") {
      this.setState(
        produce((prevState) => {
          prevState.avatar.moreDataJson = { breed: value };
        })
      );
    } else {
      this.setState(
        produce((prevState) => {
          prevState.avatar[name] = value;
        })
      );
    }
  };

  handleDateChange = (date) => {
    this.setState(
      produce((prevState) => {
        prevState.avatar.birthDate = date
          ? time(date).format("YYYY-MM-DD")
          : "";
      })
    );
  };

  onSkinSelect = (value) => {
    this.setState(
      produce((prevState) => {
        prevState.avatar.moreDataJson = { skinColor: value };
        prevState.avatar.avatarCustomization = {
          ...prevState.avatar.avatarCustomization,
          skinColor: value
        };
      })
    );
    this.didCustomize = true;
  };

  changeAvatarMode = (mode) => {
    this.setState(
      produce((prevState) => {
        prevState.avatar.previewMode = mode;
      })
    );
    this.didCustomize = true;
  };

  handleProfileImageSelection = (e) => {
    let paths;
    if (isBrowser) paths = e.target.files[0];
    else {
      paths = dialog.showOpenDialogSync({ properties: ["openFile"] });
      if (paths == null || paths[0] == null) return;
    }
    loadImage(
      isBrowser ? paths : path.join("file://", paths[0]),
      (img) => {
        const base64Data = img.toDataURL("image/jpeg");
        this.setState({
          cropper: {
            originalImageSrc: base64Data,
            croppedImageDataUrl: null,
            isOpened: true
          }
        });
      },
      { orientation: true }
    );
  };

  handleCrop = (croppedImageDataUrl) => {
    this.setState(({ cropper }) => ({
      cropper: {
        ...cropper,
        croppedImageDataUrl,
        isOpened: false
      }
    }));
  };

  showDeleteDialog = () => this.setState({ showDeleteDialog: true });
  handleDelete = async () => {
    const { objectId } = this.state.avatar;
    const { dispatch, afterDelete, app } = this.props;
    this.setState({ isDeleting: true });
    try {
      await ajax.deleteAvatar(objectId);
      dispatch(actions.deleteAvatar(objectId));
      afterDelete(objectId);

      app.synchronizer.announceItemChange(
        app.synchronizer.SynchronizableItems.AVATARS
      );
      this.setState({ avatar: getAvatarDefaults() });
    } catch (error) {
      this.setState({
        error: true,
        showDeleteDialog: false,
        isDeleting: false
      });
      log.error(error);
    }
  };

  render() {
    const {
      error,
      isProcessing,
      isDeleting,
      showDeleteDialog,
      avatar,
      cropper
    } = this.state;
    const { profileImageFile } = avatar;
    const { t, onClose, onBack } = this.props;

    let croppedAvatar = cropper.croppedImageDataUrl
      ? cropper.croppedImageDataUrl
      : null;

    return (
      <>
        <Dialog
          isOpen={isProcessing}
          processingText={t("avatar.editor.saving")}
          isProcessing={isProcessing}
        />
        <GenericFailureDialog
          onDismiss={() => this.setState({ error: false })}
          isOpen={error}
        />
        <Dialog
          isOpen={showDeleteDialog}
          heading={t("avatar.editor.deleteDialog.heading")}
          text={t("avatar.editor.deleteDialog.text")}
          isProcessing={isDeleting}
          processingText={t("avatar.deleting")}
          buttons={[
            {
              text: t("buttons.cancel"),
              onClick: () => this.setState({ showDeleteDialog: false })
            },
            {
              text: t("buttons.delete"),
              onClick: this.handleDelete
            }
          ]}
        />
        {cropper.isOpened && (
          <Cropper
            src={cropper.originalImageSrc}
            onBack={() => this.setState({ cropper: { isOpened: false } })}
            onCrop={this.handleCrop}
          />
        )}
        {!cropper.isOpened && environment.supportsBabies && (
          <BabyView
            avatar={avatar}
            croppedAvatar={croppedAvatar}
            profileImageFile={profileImageFile}
            onSkinSelect={this.onSkinSelect}
            t={t}
            onClose={onClose}
            onBack={onBack}
            onSubmit={this.handleSubmit}
            onAvatarSelection={this.handleProfileImageSelection}
            onDelete={this.showDeleteDialog}
            onInputChange={this.handleInputChange}
            handleDateChange={this.handleDateChange}
            handleAvatarStyleUpdate={this.handleAvatarStyleUpdate}
            changeAvatarMode={this.changeAvatarMode}
          />
        )}
        {!cropper.isOpened && environment.supportsDogs && (
          <DogView
            avatar={avatar}
            croppedAvatar={croppedAvatar}
            profileImageFile={profileImageFile}
            t={t}
            onClose={onClose}
            onBack={onBack}
            onSubmit={this.handleSubmit}
            onAvatarSelection={this.handleProfileImageSelection}
            onDelete={this.showDeleteDialog}
            onInputChange={this.handleInputChange}
            handleDateChange={this.handleDateChange}
            handleAvatarStyleUpdate={this.handleAvatarStyleUpdate}
            changeAvatarMode={this.changeAvatarMode}
          />
        )}
      </>
    );
  }
}

const enhance = compose(withApp, connect(), withTranslation());

export default enhance(AvatarEditor);
